'use client';

import { Button, ButtonProps, ButtonTypeMap, styled } from '@mui/material';
import { DefaultComponentProps } from '@mui/material/OverridableComponent';
import { COLOR } from '../Theme/Colors';

const DEFAULT_PROPS: DefaultComponentProps<ButtonTypeMap> = {
  size: 'large',
  variant: 'primary',
  sx: {
    textTransform: 'none',
  },
};

const StyledButton = styled(Button)(({ theme }) => ({
  background: COLOR.PRIMARY[500],
  transitionDuration: '200ms',
  borderRadius: '8px',
  fontWeight: 700,
  boxShadow: 'unset',
  '&.MuiButton-root': {
    padding: '10px 16px',
    fontSize: '14px',
    lineHeight: '20px',
    color: COLOR.GENERIC[0],
  },
  '&.MuiButton-primary': {
    background: theme.palette.green[800],
    color: COLOR.GENERIC[0],
    '&:hover': {
      background: theme.palette.green[500],
    },
    '&:active': {
      outline: `1px solid ${theme.palette.green[600]}`,
      background: theme.palette.green[800],
    },
    '&:focus': {
      boxShadow: `0 0 0 4px ${theme.palette.green[500]}`,
      outline: 'none',
    },
    '&:disabled': {
      color: COLOR.GENERIC[0],
      background: theme.palette.green[300],
    },
  },
  '&.MuiButton-secondary': {
    background: theme.palette.neutral[50],
    color: theme.palette.neutral[500],
    boxShadow: 'unset',
    '&:hover': {
      outline: `1px solid ${theme.palette.neutral[400]}`,
    },
    '&:active': {
      boxShadow: `0 0 0 4px ${COLOR.NEUTRAL[200]}`,
      color: theme.palette.neutral[400],
    },
    '&:focus': {
      boxShadow: `0 0 0 4px ${theme.palette.neutral[400]}`,
    },
    '&:disabled': {
      color: theme.palette.neutral[300],
      background: theme.palette.neutral[50],
    },
  },
  '&.MuiButton-outline': {
    color: theme.palette.green[800],
    background: 'transparent',
    outline: `1px solid ${theme.palette.green[800]}`,
    '&:hover': {
      color: theme.palette.green[500],
      outline: `1px solid ${theme.palette.green[500]}`,
    },
    '&:active': {
      outline: `1px solid ${theme.palette.green[600]}`,
      color: theme.palette.green[600],
    },
    '&:focus': {
      boxShadow: `0 0 0 3px ${theme.palette.green[500]}`,
    },
    '&:disabled': {
      color: theme.palette.green[300],
      outline: `1px solid ${theme.palette.green[300]}`,
    },
  },
  '&.MuiButton-link': {
    color: theme.palette.green[800],
    background: 'transparent',
    '&:hover': {
      color: theme.palette.green[500],
    },
    '&:active': {
      color: theme.palette.green[600],
    },
    '&:focus': {
      color: theme.palette.green[800],
      outline: `1px solid ${theme.palette.green[800]}`,
    },
    '&:disabled': {
      color: theme.palette.green[300],
    },
  },
  '&.MuiButton-sizeXl': {
    padding: '24px 48px',
    fontSize: '18px',
    lineHeight: '36px',
  },
  '&.MuiButton-sizeLg': {
    padding: '10px 16px',
    fontSize: '16px',
    lineHeight: '28px',
  },
  '&.MuiButton-sizeMd': {
    padding: '10px 16px',
    fontSize: '14px',
    lineHeight: '20px',
  },
  '&.MuiButton-sizeSm': {
    padding: '8px 12px',
    fontSize: '12px',
    lineHeight: '14px',
  },
}));

const MuiButton: React.FC<ButtonProps> = (props) => <StyledButton {...DEFAULT_PROPS} data-testname="mui-button" {...props} />;

export default MuiButton;
